import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useIntl} from 'react-intl';
import {toast} from 'react-toastify';

import {useLoadFile} from 'utils/hooks/useLoadFile';

import './styles.scss';
import {IArticleCard} from '../../redux/content/interfaces';
import {
  addFavoriteCardAction,
  addMaterialAction,
  removeFavoriteCardAction,
  removeMaterialAction
} from '../../redux/content/contentAsyncActions';
import Clock from '../../assets/image/library/clock.svg';
import Book from '../../assets/image/library/book.svg';
import {ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';
import {ReactComponent as CheckImg} from 'assets/image/library/ok-circle.svg';
import {ReactComponent as EmptyHeart} from 'assets/image/library/empty-heart.svg';
import {ReactComponent as FullHeart} from 'assets/image/library/full-heart.svg';

const MAX_VISIBLE_TAGS = 3;

type Props = {
  card: IArticleCard;
  active?: boolean;
  checked?: boolean;
  disabledButtons?: boolean;
  showMaterialsButton?: boolean;
  isNew?: boolean;
  menu?: React.ReactNode;
  onClick: () => void;
}

export const MnArticleCardCard = ({card,
  active = false,
  checked = false,
  disabledButtons = false,
  showMaterialsButton,
  isNew = false,
  menu,
  onClick}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const {getFileUrl} = useLoadFile();
  const [imageUrl, setImageUrl] = useState<string | null>(card.pictureUrl || null);
  const [isSave, setIsSave] = useState(card.material);
  const [favorite, setFavorite] = useState(card.favorite);

  useEffect(() => {
    if (!card.pictureUrl && card.picture) {
      getFileUrl(card.picture, (url) => {
        if (url) {
          setImageUrl(url);
        }
      });
    } else {
      setImageUrl(card.pictureUrl || null);
    }
  }, [card.pictureUrl, card.pictureUrl]);

  useEffect(() => {
    setIsSave(card.material);
  }, [card.material]);

  useEffect(() => {
    setFavorite(card.favorite);
  }, [card.favorite]);

  const lifeSituations = card.lifeSituations
    ? card.lifeSituations.split(',').map(t => t.trim()).filter(Boolean)
    : [];
  const visibleLifeSituations = lifeSituations.slice(0, MAX_VISIBLE_TAGS);
  const hiddenLifeSituationsCount = lifeSituations.length - MAX_VISIBLE_TAGS;

  const renderLifeSituations = () => {
    return visibleLifeSituations.length > 0 && (
      <div className="mn-article-card__tags">
        {visibleLifeSituations.map((tag, i) => (
          <span key={i} className="mn-article-card__tag">{tag}</span>
        ))}
        {hiddenLifeSituationsCount > 0 && (
          <span className="mn-article-card__tag mn-article-card__tag--overflow">+{hiddenLifeSituationsCount}</span>
        )}
      </div>
    );
  };

  function addToMaterials() {
    setIsSave(!isSave);
    if (!isSave) {
      dispatch(addMaterialAction(card.id));
    } else {
      dispatch(removeMaterialAction([card.id]));
    }
  }

  async function handleFavoriteClick(e: React.MouseEvent) {
    e.stopPropagation();
    const next = !favorite;

    setFavorite(next);

    const success = next
      ? await dispatch(addFavoriteCardAction(card.id))
      : await dispatch(removeFavoriteCardAction(card.id));

    if (!success) {
      setFavorite(!next);

      return;
    }

    if (next) {
      toast.success(intl.formatMessage({
        id: 'gritx.common.card.addedToFavorites',
        defaultMessage: 'Added to Favorites'
      }));
    }
  }

  const renderHeartButton = () => {
    return (
      <button
        type="button"
        className="mn-article-card__heart-btn"
        onClick={handleFavoriteClick}
        aria-label={favorite
          ? intl.formatMessage({id: 'gritx.common.card.removeFromFavorites', defaultMessage: 'Remove from Favorites'})
          : intl.formatMessage({id: 'gritx.common.card.addToFavorites', defaultMessage: 'Add to Favorites'})}
      >
        {favorite
          ? <FullHeart className="mn-article-card__heart-btn-icon"/>
          : <EmptyHeart className="mn-article-card__heart-btn-icon"/>}
      </button>
    );
  };

  const renderMaterialsButton = () => {
    return showMaterialsButton ? <GritxButton
      title={card.material ? intl.formatMessage({id: 'gritx.common.card.saved', defaultMessage: 'Saved'})
        : intl.formatMessage({id: 'gritx.common.card.addToMaterials', defaultMessage: 'Add to Materials'})}
      variant={ButtonVariant.Primary}
      onClick={addToMaterials}/> : null;
  };

  function renderCheckedCard() {
    return <><div className="mn-article-card-accent"></div>
      <div className="mn-article-card" onClick={onClick} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick()}>
        <div className="mn-article-card__body">
          <div className="mn-article-card__title">{card.title}</div>
          <div className="mn-article-card__check-img-wrapper">
            <CheckImg className="mn-article-card__check-img"/>
          </div>
        </div>
      </div>
    </>;
  }

  if (checked) {
    return renderCheckedCard();
  }

  return (
    <>
      <div className="mn-article-card-accent"></div>
      <div className="mn-article-card">
        {menu || renderHeartButton()}
        {/* <div className="mn-article-card__header" onClick={onClick} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick()}>*/}
        {/*  <div*/}
        {/*    className="mn-article-card__item-img"*/}
        {/*    style={{backgroundImage: `url('${card.labelPictureUrl}')`}}*/}
        {/*    title={card.labelText}*/}
        {/*  />*/}
        {/*  {card.readingName && (*/}
        {/*    <div*/}
        {/*      className="mn-article-card__item-img"*/}
        {/*      style={{backgroundImage: `url('${card.readingImageUrl || Clock}')`}}*/}
        {/*      title={card.readingName}*/}
        {/*    />*/}
        {/*  )}*/}
        {/* </div>*/}

        <div className="mn-article-card__body" onClick={onClick} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick()}>
          <div className="mn-article-card__title">{card.title}</div>
          <div className="mn-article-card__card-picture">
            <img src={card.pictureUrl || Book} role="presentation" alt="" className="mn-article-card__image"/>
          </div>
          {card.description && (
            <div className="mn-article-card__org">{card.description}</div>
          )}

          {renderLifeSituations()}
        </div>

        <div className="mn-article-card__footer">
          {renderMaterialsButton()}
        </div>
      </div>
    </>
  );
};
